import { FC } from 'react';
import OpmTotalEquityChart from '@app/modules/projects/dashboard/opm-widgets/total-equity/OpmTotalEquityChart';
import { DashboardWidgetContainer } from '@app/shared/components/dashboard-widget-container/DashboardWidgetContainer';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  amountRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { useOpmTotalEquityGeneralMetrics } from './useOpmTotalEquityGeneralMetrics';
import { useGetOpmForecasts } from '@app/core/store/opm-calculation-slice-selectors';
import { useLocale } from '@app/core/hooks/useLocale';
import styles from '@app/modules/projects/outputs/real-world-distributions/real-world-distributions.module.scss';
import { useAppSelector } from '@app/core/hooks/redux-hooks';

const OpmTotalEquity: FC = (): JSX.Element => {
  const { l } = useLocale();
  const { getOpmForecasts, getOpmEntryData } = useGetOpmForecasts();
  const forecasts = getOpmForecasts();
  const inputSummary = useAppSelector((state) => state.opmCalculation.riskFreeValues.inputSummary);
  const entryData = getOpmEntryData();

  const {
    entryTotalEquityValue,
    equityProceedsWeightedValue,
    exitSlnDebtProceeds,
    exitOrdinaryEquityProceeds,
    sweetEquityPercentageOfOrdinaryEquityProceeds,
  } = useOpmTotalEquityGeneralMetrics(forecasts);

  return (
    <DashboardWidgetContainer
      title={l('_TotalEquityTitle')}
      className={styles['mt-20']}
      isExpanded
      secondaryContent={
        <table className="table-primary table-primary--fixed table-primary--secondary-theme">
          <thead>
            <tr>
              <th className="table-primary__cell--header-quinary" style={{ minWidth: '120px' }}>
                {l('_DayOneEquityValue')}
              </th>
              <th className="table-primary__cell--header-quinary" style={{ minWidth: '120px' }}>
                {l('_CurrentEquityValue')}
              </th>
              <th className="table-primary__cell--header-quinary" style={{ minWidth: '120px' }}>
                {l('_WeightedExitEquityValue')}
              </th>
              <th className="table-primary__cell--header-quinary" style={{ minWidth: '120px' }}>
                {l('_WeightedExitShareholderDebtProceeds')}
              </th>
              <th className="table-primary__cell--header-quinary" style={{ minWidth: '140px' }}>
                {l('_WeightedExitOrdinaryEquityProceeds')}
              </th>
              <th className="table-primary__cell--header-quinary" style={{ minWidth: '167px' }}>
                {l('_WeightedSweetEquityPercentageOfOrdinaryEquityProceeds')}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <CellValue
                  {...amountRules}
                  value={entryTotalEquityValue}
                  className="heading-2 heading-2--alternate"
                  alignment={Alignment.Left}
                />
              </td>
              <td>
                <CellValue
                  {...amountRules}
                  value={inputSummary.currentEvOrEquity}
                  className="heading-2 heading-2--alternate"
                  alignment={Alignment.Left}
                />
              </td>
              <td>
                <CellValue
                  {...amountRules}
                  value={equityProceedsWeightedValue}
                  className="heading-2 heading-2--alternate"
                  alignment={Alignment.Left}
                />
              </td>
              <td>
                <CellValue
                  {...amountRules}
                  value={exitSlnDebtProceeds}
                  className="heading-2 heading-2--alternate"
                  alignment={Alignment.Left}
                />
              </td>
              <td>
                <CellValue
                  {...amountRules}
                  value={exitOrdinaryEquityProceeds}
                  className="heading-2 heading-2--alternate"
                  alignment={Alignment.Left}
                />
              </td>
              <td>
                <CellValue
                  {...enteredPercentageViewRules}
                  value={sweetEquityPercentageOfOrdinaryEquityProceeds}
                  className="heading-2 heading-2--alternate"
                  alignment={Alignment.Left}
                />
              </td>
            </tr>
          </tbody>
        </table>
      }>
      {(Boolean(entryTotalEquityValue) || entryTotalEquityValue === 0) &&
        (!Number.isNaN(equityProceedsWeightedValue) || equityProceedsWeightedValue === 0) &&
        (Boolean(exitSlnDebtProceeds) || exitSlnDebtProceeds === 0) &&
        (Boolean(exitOrdinaryEquityProceeds) || exitOrdinaryEquityProceeds === 0) &&
        forecasts.length > 0 && (
          <OpmTotalEquityChart entryData={entryData} forecastData={forecasts} />
        )}
    </DashboardWidgetContainer>
  );
};

export default OpmTotalEquity;
